import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import DataMap from 'assets/components/dataMap/DataMap';
import useForm from 'assets/components/form/hooks/Form';
import Icon from 'assets/components/icon/Icon';
import CheckInput from 'assets/components/inputs/check/CheckInput';
import DateInput from 'assets/components/inputs/dateInput/DateInput';
import FileInput from 'assets/components/inputs/file/FileInput';
import ThemeFileInputDropzone from 'assets/components/inputs/file/themes/Theme.FileInput.Dropzone';
import { inputStyles } from 'assets/components/inputs/Input';
import MultilineTextInput from 'assets/components/inputs/multilineText/MultilineTextInput';
import NumberInput from 'assets/components/inputs/number/NumberInput';
import SelectInput from 'assets/components/inputs/select/SelectInput';
import TextInput from 'assets/components/inputs/text/TextInput';
import FormWrapper from 'assets/components/inputs/wrappers/FormWrapper';
import InputWrapper from 'assets/components/inputs/wrappers/InputWrapper';
import { getLocales } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import ThemeWrapperPaper from 'assets/themes/wrappers/Theme.Wrapper.Paper';
import { switchPlacesInArray } from 'assets/utils/data/Data';
import { defaultDateFormat, defaultDateTimeFormat } from 'assets/utils/data/Date';
import { isEmptyValue } from 'assets/utils/data/Object';
import { combineStrings } from 'assets/utils/data/String';
import { createPathFromRoute } from 'assets/utils/dom/UrlParsing';
import { sortBy } from 'lodash';
import InputType from 'models/enums/InputType';
import ProductDocumentPropertyPath from 'models/enums/ProductDocumentPropertyPath';
import ProductDocumentPreview from 'module/productManagement/components/productDocuments/ProductDocumentPreview';
import { productEnhancementDocumentIndexRoute } from 'module/productManagement/views/productEnhancementDocument/ProductEnhancementDocument.Index';
import { Fragment, useEffect, useState } from 'react';
import { useProductEnhancementProvider } from '../../ProductEnhancement.Hooks';

type AdditionalFieldProps = {
  additionalField: Utils.FormData<Model.IProductFillablePdfField>;
  onValueChange(data: Partial<Model.IProductFillablePdfField>): void;
};
function DefaultValueInput({ onValueChange, additionalField }: AdditionalFieldProps) {
  const { lang } = getLocales();
  return (
    <>
      {additionalField.type === InputType.text && (
        <TextInput
          name="defaultValue"
          label={lang.default}
          value={typeof additionalField?.defaultValue === 'string' ? additionalField?.defaultValue : null}
          onChange={onValueChange}
        />
      )}
      {additionalField.type === InputType.longText && (
        <MultilineTextInput
          name="defaultValue"
          label={lang.default}
          value={typeof additionalField?.defaultValue === 'string' ? additionalField?.defaultValue : null}
          onChange={onValueChange}
        />
      )}
      {additionalField.type === InputType.boolean && (
        <CheckInput
          name="defaultValue"
          label={lang.default}
          value={typeof additionalField?.defaultValue === 'boolean' ? additionalField?.defaultValue : null}
          onChange={onValueChange}
          class={inputStyles.check.switch}
        />
      )}
      {(additionalField.type === InputType.date || additionalField.type === InputType.dateTime) && (
        <DateInput
          name="defaultValue"
          label={lang.default}
          value={typeof additionalField?.defaultValue === 'string' ? additionalField?.defaultValue : null}
          onChange={onValueChange}
          dateTimeFormat={additionalField.type === InputType.dateTime ? defaultDateTimeFormat : defaultDateFormat}
        />
      )}
      {(additionalField.type === InputType.number || additionalField.type === InputType.decimal) && (
        <NumberInput
          name="defaultValue"
          label={lang.default}
          value={typeof additionalField?.defaultValue === 'number' ? additionalField?.defaultValue : null}
          step={additionalField.type === InputType.number ? 1 : 0.01}
          onChange={onValueChange}
        />
      )}
      {additionalField.type === InputType.year && (
        <NumberInput
          name="defaultValue"
          label={lang.default}
          value={typeof additionalField?.defaultValue === 'number' ? additionalField?.defaultValue : null}
          step={1}
          min={1000}
          max={9999}
          onChange={onValueChange}
        />
      )}
      {additionalField.type === InputType.month && (
        <NumberInput
          name="defaultValue"
          label={lang.default}
          value={typeof additionalField?.defaultValue === 'number' ? additionalField?.defaultValue : null}
          step={1}
          min={1}
          max={12}
          onChange={onValueChange}
        />
      )}
      {additionalField.type === InputType.day && (
        <NumberInput
          name="defaultValue"
          label={lang.default}
          value={typeof additionalField?.defaultValue === 'number' ? additionalField?.defaultValue : null}
          step={1}
          min={1}
          max={31}
          onChange={onValueChange}
        />
      )}
    </>
  );
}
export default function ProductEnhancementEditDocuments() {
  const { lang, translate } = getLocales();
  const { form, uploadedDocuments, newDocuments, productEnhancement, uploadFile, deleteFile } =
    useProductEnhancementProvider();

  const [documentCategory, setdocumentCategory] = useState<Model.Enum.EngineType>('Internal Combustion');
  const docForm = useForm<Utils.FormData<Model.IProductEnhancementDocument & { isFileRepalced?: boolean }>>({
    default: {},
    onSubmit: (document) => {
      const index = form.data.productEnhancementDocuments?.findIndex((p) => p.id === document.id);
      form.data.productEnhancementDocuments.splice(index, 1, document);
      form.update({
        productEnhancementDocuments: [...form.data.productEnhancementDocuments],
      });
      docForm.reload();
    },
  });

  useEffect(() => {
    if (docForm.data.productEnhancementAdditionalFields) return;
    const doc = productEnhancement?.productEnhancementDocuments?.find((d) => d.id === docForm.data.id);
    if (doc) docForm.set(doc);
  }, [productEnhancement, docForm.data.id]);

  function updateAdditionalField(additionalField: Utils.FormData<Model.IProductFillablePdfField>) {
    const index = docForm.data.productEnhancementAdditionalFields?.findIndex((p) => p.id === additionalField.id);
    docForm.data.productEnhancementAdditionalFields.splice(index, 1, additionalField);
    docForm.update({
      productEnhancementAdditionalFields: [...docForm.data.productEnhancementAdditionalFields],
    });
  }
  const additionalFields = docForm.data.productEnhancementAdditionalFields?.filter((f) => f.key);

  const allFiles = sortBy([...uploadedDocuments, ...newDocuments], (it) => it.orderNumber);
  function reorderfile(index: number, delta: -1 | 1) {
    form.update({
      productEnhancementDocuments: switchPlacesInArray(allFiles, index, delta).map((it, i) => ({
        ...it,
        orderNumber: i + 1,
      })),
    });
  }
  return (
    <>
      <ProductDocumentPreview
        children={
          <>
            <div
              style={{
                fontSize: Theme.FontSize.L,
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              {lang.documents}
            </div>
            <div
              style={{
                padding: Theme.Size.M,
                display: docForm.data.id ? 'none' : undefined,
              }}
            >
              <CheckInput
                onChange={({ isForElectricVehicle }) => {
                  setdocumentCategory(isForElectricVehicle ? 'Electric' : 'Internal Combustion');
                }}
                name="isForElectricVehicle"
                label={lang.isForElectricVehicle}
                class={inputStyles.check.switch}
                value={documentCategory === 'Electric'}
              />
              <br />
              <FileInput
                name="file"
                fileLimit={1}
                onChange={async ({ file }) => {
                  const formFile = file && file[0]?.original;
                  uploadFile({ formFile, documentCategory, orderNumber: allFiles.length + 1 });
                }}
                class={ThemeFileInputDropzone}
                error={docForm.errors.formFile?._objectError}
                disabled={!form.data?.id}
              />
            </div>
          </>
        }
        documents={allFiles.map((it, i) => ({
          id: it.id,
          orderNumber: it.orderNumber,
          url: it.mediaFile.url,
          infoUrl: createPathFromRoute(productEnhancementDocumentIndexRoute, { id: it?.id }),
          name: it.mediaFile?.name || lang.unknownDocument,
          info: !it.id ? lang.new : lang.uploaded,
          description: it.documentCategory,
          isEditing: docForm.data.id === it.id,
          isDeletable: (it.productEnhancementDealerDocuments?.length ?? 0) === 0,
          onClick: !it.id ? undefined : () => (docForm.data.id === it.id ? docForm.reload() : docForm.set(it)),
          onDelete: !it.id ? undefined : () => deleteFile(it.id),
          onReorder: docForm.data.id ? undefined : (delta) => reorderfile(i, delta),
        }))}
      />
      <br />
      {!isEmptyValue(docForm.data) && (
        <FormWrapper
          class={ThemeWrapperPaper}
          children={
            <>
              <h2>{lang.replaceFile}</h2>
              <FileInput
                name="file"
                fileLimit={1}
                onChange={async ({ file }) => {
                  const formFile = file && file[0]?.original;
                  docForm.update({ formFile, isFileRepalced: true });
                }}
                class={ThemeFileInputDropzone}
                error={docForm.errors.formFile?._objectError}
                disabled={!form.data?.id}
                value={
                  docForm.data.formFile
                    ? [
                        {
                          original: docForm.data.formFile,
                          type: docForm.data.formFile?.type as Utils.File.MimeType,
                          dataUrl: window.URL.createObjectURL(docForm.data.formFile),
                        },
                      ]
                    : undefined
                }
              />
              {additionalFields && (
                <>
                  <h2>{docForm.data.mediaFile?.name}</h2>
                  <DataMap
                    data={additionalFields}
                    render={({ data: additionalField }) => {
                      const fieldType = additionalField.isDisabled ? 'disabled' : 'enabled';
                      return (
                        <Fragment key={additionalField.id}>
                          <SelectInput
                            name="type"
                            data={[
                              { id: 'enabled', title: combineStrings(' | ', additionalField.key, lang.enabled) },
                              { id: 'disabled', title: combineStrings(' | ', additionalField.key, lang.disabled) },
                              { id: 'signature', title: combineStrings(' | ', additionalField.key, lang.signature) },
                            ]}
                            value={fieldType}
                            style={{
                              input:
                                fieldType === 'enabled'
                                  ? {
                                      backgroundColor: Theme.Color.success,
                                      color: 'white',
                                    }
                                  : fieldType === 'disabled'
                                    ? {
                                        backgroundColor: Theme.Color.error,
                                        color: 'white',
                                      }
                                    : {
                                        backgroundColor: Theme.Color.primary,
                                        color: 'white',
                                      },
                            }}
                            onChange={({ type }) => {
                              if (type === 'enabled' || !type) {
                                updateAdditionalField({ ...additionalField, isDisabled: false });
                              } else if (type === 'disabled') {
                                updateAdditionalField({ ...additionalField, isDisabled: true });
                              } else {
                                updateAdditionalField({ ...additionalField, isDisabled: true });
                              }
                            }}
                          />
                          {!additionalField.isDisabled && (
                            <>
                              <InputWrapper>
                                <SelectInput
                                  name="propertyPath"
                                  label={lang.property}
                                  data={Object.values(ProductDocumentPropertyPath).map((val) => ({
                                    id: val,
                                    title: val,
                                  }))}
                                  value={additionalField.propertyPath}
                                  onChange={(updated) =>
                                    updateAdditionalField({ ...additionalField, ...updated, defaultValue: null })
                                  }
                                />
                                <SelectInput
                                  name="type"
                                  label={lang.type}
                                  data={Object.values(InputType).map((val) => ({ id: val, title: translate(val) }))}
                                  value={additionalField.type}
                                  onChange={(updated) =>
                                    updateAdditionalField({ ...additionalField, ...updated, defaultValue: null })
                                  }
                                />
                              </InputWrapper>
                              <InputWrapper>
                                <TextInput
                                  name="group"
                                  label={lang.group}
                                  value={additionalField.group}
                                  onChange={(updated) => updateAdditionalField({ ...additionalField, ...updated })}
                                />
                                <TextInput
                                  name="label"
                                  label={lang.label}
                                  value={additionalField.label}
                                  onChange={(updated) => updateAdditionalField({ ...additionalField, ...updated })}
                                />
                              </InputWrapper>
                              <DefaultValueInput
                                additionalField={additionalField}
                                onValueChange={(updated) => updateAdditionalField({ ...additionalField, ...updated })}
                              />
                              <CheckInput
                                name="isRequired"
                                label={lang.isRequired}
                                value={additionalField.isRequired}
                                onChange={(updated) => updateAdditionalField({ ...additionalField, ...updated })}
                                class={inputStyles.check.switch}
                              />
                            </>
                          )}
                        </Fragment>
                      );
                    }}
                  />
                </>
              )}
              <Button
                render={lang.save}
                media={<Icon class="fas-save" />}
                onClick={docForm.submit}
                disabled={docForm.hasErrors}
                active={!docForm.hasErrors}
                class={ThemeButtonCircle}
                htmlElementProps={{ title: lang.save }}
              />
            </>
          }
        />
      )}
    </>
  );
}
